import { useCopilotSessionContext } from '@/features/copilot';
import { open as openSidebar } from '@/features/sidebar';
import { useFeatureFlag, FeatureFlag } from '@/services/auth/feature-flags';
import { useAnalytics } from '@/services/analytics';

export type AskAgentPanel = 'alerts' | 'data_alerts' | 'recommendations';

const WORKSPACE_HEALTH_PROMPT = 'Help me understand the current issues in my workspace health.';

/**
 * Powers the panel-level "Ask agent" CTA on workspace health panels.
 *
 * `enabled` gates the CTA on `workspace_health_ask_agent && ai_enabled`.
 * `askAgent` tracks analytics, resets the copilot session, opens the sidebar,
 * and dispatches a workspace-wide diagnostic prompt.
 */
export function useAskAgentForWorkspaceHealth(panel: AskAgentPanel): {
  enabled: boolean;
  askAgent: () => void;
} {
  const session = useCopilotSessionContext();
  const aiEnabled = useFeatureFlag(FeatureFlag.AiEnabled);
  const enabled = useFeatureFlag(FeatureFlag.WorkspaceHealthAskAgent) && aiEnabled;
  const { track } = useAnalytics();

  const askAgent = () => {
    void track('workspaceHealth', 'askAgentClicked', { Panel: panel });
    const newSession = session.reset();
    openSidebar('copilot');
    void newSession.sendMessage(WORKSPACE_HEALTH_PROMPT);
  };

  return { enabled, askAgent };
}
